import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/Details.css";
import { Table, Spin, Tag, Modal, Input } from "antd";
import useDebounce from '../hooks/useDebounce';
import { getParticipationMatrix2Data } from '../services/ParticipationMatrix2Service';
const { Column } = Table;
const { Search } = Input;

function ParticipationMatrix2() {
    document.title = "LunaJoy Dashboard";
    const navigate = useNavigate()
    const [totalProviders, setTotalProviders] = useState(0);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [participationMatrix2Data, setParticipationMatrix2Data] = useState([]);

    const searchProviders = async (filters = {}) => {
        try {
          setLoading(true)
          // Extract the searchField for client-side filtering
          const searchField = filters.searchField;
          
          // If there's a searchField, get all providers and filter client-side
          if (searchField) {
            const searchTerm = searchField.toLowerCase();
            
            return participationMatrix2Data?.filter(pmData => 
              (pmData.provider && pmData.provider.toLowerCase().includes(searchTerm))
            );
          }
      
          return []
        } catch (error) {
          console.error('Error searching providers:', error);
          throw error;
        } finally {
          setLoading(false)
        }
      };

    async function loadData(filters = {}) {
        setLoading(true);

        try {
            // Use search endpoint if filters are provided, otherwise get all providers
            let pmData;
            if (Object.keys(filters).length > 0) {
                pmData = await searchProviders(filters);
            } else {
                pmData = await getParticipationMatrix2Data();
            }
            setTotalProviders(
                pmData.length || 0
            );

            if (pmData.length > 0) {
                setParticipationMatrix2Data(pmData)

                // Flatten the data into rows
                const flattenedData = pmData.flatMap(provider =>
                    provider?.participation?.map((stateInfo, index) => ({
                        key: `${provider.key}-${index}`,
                        provider: provider.provider,
                        state: stateInfo.state,
                        insurances: stateInfo.insurances,
                        providerRowSpan: index === 0 ? provider.participation.length : 0,
                    }))
                );

                setData(flattenedData); // Set only the paginated rows to data
            }
        } catch (error) {
            Modal.confirm({
                title: "Error Message",
                content: error?.response?.data?.message || "Oops! Error in Fetching Providers data",
                okText: "Ok",
                onOk: async () => {
                    console.error("Error in Fetching Providers data");
                },
                cancelButtonProps: { style: { display: "none" } },
            });
        } finally {
            setLoading(false)
        }
    }

    const handleSearch = (value) => {
        setSearchTerm(value);
    };

    const debouncedSearchValue = useDebounce(searchTerm);

    useEffect(() => {
        setData([])

        const filters = {}
    
        if (debouncedSearchValue.length !== 0) {
            filters['searchField'] = debouncedSearchValue
        }
        loadData(filters)
    }, [debouncedSearchValue]);

    return (
        <div id="dashboard-container">
            <div className="row styleFilterRow">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontWeight: 500 }}>
                            Total Providers
                        </span>
                        <Tag>
                            <strong style={{ fontSize: 16 }}>{totalProviders}</strong>
                        </Tag>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                        <Search
                            id="search"
                            placeholder="Search by Provider name"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onSearch={handleSearch}
                            allowClear
                            disabled={loading}
                            style={{ width: "25vw" }}
                        />
                    </div>
                </div>
            </div>

            <Spin spinning={loading}>
                <Table
                    dataSource={data}
                    pagination={false}
                    bordered={true}
                    // onChange={handleTableChange}
                    scroll={{ y: "65vh" }}
                >
                    <Column
                        title='Provider'
                        dataIndex='provider'
                        key='provider'
                        width={260}
                        render={(text, record, index) => {
                            const rowSpan = record.providerRowSpan; // Get rowSpan from data
                            return {
                                children: text,
                                props: { rowSpan: rowSpan }, // Apply rowSpan
                            }
                        }}
                    />
                    <Column
                        title='State'
                        dataIndex='state'
                        key='state'
                        width={200}
                    />
                    <Column
                        title='Insurances'
                        dataIndex='insurances'
                        key='insurances'
                        render={(insurances) => (insurances.map((insurance, index) => {
                            function getRandomColor() {
                                const colors = [
                                    "#3498db", // Light Blue
                                    "#e74c3c", // Red
                                    "#2ecc71", // Green
                                    "#f1c40f", // Yellow
                                    "#9b59b6", // Purple
                                    "#1abc9c", // Teal
                                    "#e67e22", // Orange
                                    "#34495e", // Dark Blue
                                    "#e84393", // Pink
                                    "#8e44ad", // Dark Purple
                                    "#f39c12", // Dark Yellow/Gold
                                    "#27ae60", // Emerald Green
                                    "#c0392b", // Dark Red
                                    "#2980b9"  // Ocean Blue
                                ];

                                const randomIndex = Math.floor(Math.random() * colors.length);
                                return colors[randomIndex];
                            }

                            return <Tag key={insurance} style={{ marginBlock: '4px', borderColor: getRandomColor(), padding: '3px 12px', borderWidth: '2px' }} >{insurance}</Tag>
                        })
                        )
                        }
                    />
                </Table>
            </Spin>
        </div>
    );
}

export default ParticipationMatrix2;
